import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { isDefined } from '../../../../utils/variables';
import { useAppContext, UserRole } from '../../../state/app-state';
import { useProjectAccess } from '../../../state/project-access-state';
import { ProjectContext } from '../../../state/project-state/project-state';
import { useTheme } from '../../../state/theme-state';
import { reset } from '../../../utils/styled-reset';
import { Card } from './card';

type OverviewProps = {
  showTitle?: boolean;
};

const Overview = ({ showTitle = true }: OverviewProps) => {
  const { t } = useTranslation('navigation');
  const { project } = React.useContext(ProjectContext);
  const { currentUser } = useAppContext();
  const theme = useTheme();
  const { hasAccessToRoute } = theme;
  const { projectAccess } = useProjectAccess();
  const navigate = useNavigate();
  const isProjectUser = currentUser?.selectedRole === UserRole.ProjectUser;
  const isDesignLicenseRestricted =
    !projectAccess?.isOrgAdmin &&
    projectAccess?.isDesignLicenseOnly !== false &&
    !theme.isMobaTheme();

  return (
    <Component hasDescription={isDefined(project.description)}>
      {showTitle && <Title className="notranslate">{project.name}</Title>}

      {isDefined(project.description) && project.description?.trim() !== '' && (
        <span className="notranslate">{project.description}</span>
      )}

      <section>
        {hasAccessToRoute(`/projects/${project.id}/viewer`) && (
          <Card
            big={true}
            disableGoogleTranslate={true}
            icon={'globe'}
            title={t('menu.skyview.title', { ns: 'navigation' })}
            onClick={() => void navigate(`/projects/${project.id}/viewer`)}
          />
        )}

        {!isProjectUser &&
          !isDesignLicenseRestricted &&
          hasAccessToRoute(`/projects/${project.id}/geodata`) && (
            <Card
              big={true}
              icon={'cloud'}
              title={t('menu.cloudProcessing.title', { ns: 'navigation' })}
              onClick={() => void navigate(`/projects/${project.id}/geodata`)}
            />
          )}
      </section>

      <section>
        {hasAccessToRoute(`/projects/${project.id}/filemanager`) && (
          <Card
            icon={'cabinet-filing'}
            title={t('menu.fileManager.title', { ns: 'navigation' })}
            onClick={() => void navigate(`/projects/${project.id}/filemanager`)}
          />
        )}

        {!isProjectUser &&
          !isDesignLicenseRestricted &&
          hasAccessToRoute(`/projects/${project.id}/timeline`) && (
            <Card
              icon={'clock'}
              title={t('menu.timeline.title', { ns: 'navigation' })}
              onClick={() => void navigate(`/projects/${project.id}/timeline`)}
            />
          )}

        {hasAccessToRoute(`/projects/${project.id}/users`) && (
          <Card
            icon={'user'}
            title={t('menu.projectAttendee.title', { ns: 'navigation' })}
            onClick={() => void navigate(`/projects/${project.id}/users`)}
          />
        )}

        {hasAccessToRoute(`/projects/${project.id}/email`) && (
          <Card
            icon={'envelope'}
            title={t('menu.email.title', { ns: 'navigation' })}
            onClick={() => void navigate(`/projects/${project.id}/email`)}
          />
        )}

        {!isProjectUser &&
          !isDesignLicenseRestricted &&
          hasAccessToRoute(`/projects/${project.id}/integrations`) && (
            <Card
              disableGoogleTranslate={true}
              icon={'puzzle-piece'}
              title={t('menu.integrations.title', { ns: 'navigation' })}
              onClick={() => void navigate(`/projects/${project.id}/integrations`)}
            />
          )}

        {!isProjectUser && hasAccessToRoute(`/projects/${project.id}/settings`) && (
          <Card
            icon={'cog'}
            title={t('menu.settings.title', { ns: 'navigation' })}
            onClick={() => void navigate(`/projects/${project.id}/settings`)}
          />
        )}
      </section>
    </Component>
  );
};

const Component = styled.div<{ hasDescription: boolean }>`
  ${reset}

  > span {
    font-size: 0.9em;
    font-weight: 500;

    ${(props) =>
      props.hasDescription &&
      css`
        font-style: italic;
      `}
  }

  section {
    display: flex;
    flex-wrap: wrap;
    gap: 1em;
    margin-top: 2em;
  }
`;

const Title = styled.h3`
  margin-bottom: 1em;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 1.2em;
  font-weight: bold;
  color: ${({ theme }) => theme.color.gray.darkest};
`;

export { Overview };
